/**
 * Project Selector Dialog
 * 
 * Shown when the app starts to let users:
 * - Create a new project
 * - Open an existing project
 * - Delete projects
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  CircularProgress,
  Divider,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SignpostIcon from '@mui/icons-material/Signpost';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { useAuth } from '../hooks/useAuth';
import { getUserProjects, deleteProject, createProject } from '../services/projects';
import type { ProjectListItem } from '../types/project';
import { DEFAULT_PROJECT_DATA } from '../types/project';

interface ProjectSelectorProps {
  open: boolean;
  onClose: () => void;
  onProjectSelect: (projectId: string) => void;
  onNewProject: (projectId: string, projectName: string) => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  open,
  onClose,
  onProjectSelect,
  onNewProject,
}) => {
  const { getToken } = useAuth();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Load projects
  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (token) {
        const projectList = await getUserProjects(token);
        setProjects(projectList);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (open) {
      loadProjects();
    }
  }, [open, loadProjects]);

  // Create new project
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    
    setCreating(true);
    try {
      const token = await getToken();
      if (token) {
        const result = await createProject(token, {
          name: newProjectName.trim(),
          data: DEFAULT_PROJECT_DATA,
        });
        onNewProject(result.id, newProjectName.trim());
        setShowNewProjectDialog(false);
        setNewProjectName('');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setCreating(false);
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId: string) => {
    try {
      const token = await getToken();
      if (token) {
        await deleteProject(token, projectId);
        setProjects(prev => prev.filter(p => p.id !== projectId));
        setDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <>
      <Dialog 
        open={open && !showNewProjectDialog} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: 400,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon sx={{ color: '#6366F1' }} />
            <Typography variant="h6" fontWeight={600}>
              Traffic Designer
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create a new project or continue where you left off
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* New Project Button */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              cursor: 'pointer',
              borderStyle: 'dashed',
              borderColor: '#6366F1',
              backgroundColor: 'rgba(99, 102, 241, 0.04)',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                borderColor: '#4F46E5',
              },
            }}
            onClick={() => setShowNewProjectDialog(true)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: '#6366F1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#4F46E5' }}>
                  New Project
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start a fresh traffic control plan
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Divider sx={{ mb: 2 }}>
            <Chip label="Recent Projects" size="small" />
          </Divider>

          {/* Projects List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : projects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No saved projects yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ mx: -2 }}>
              {projects.map((project) => (
                <ListItem
                  key={project.id}
                  disablePadding
                  secondaryAction={
                    deleteConfirm === project.id ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteProject(project.id)}
                        >
                          Delete
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip title="Delete project">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(project.id);
                          }}
                          sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemButton
                    onClick={() => onProjectSelect(project.id)}
                    sx={{ borderRadius: 1, mx: 1 }}
                  >
                    <ListItemIcon>
                      <FolderIcon sx={{ color: '#F59E0B' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={600}>
                          {project.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(project.updated_at)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SignpostIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                            <Typography variant="caption" color="text.secondary">
                              {project.sign_count} signs
                            </Typography>
                          </Box>
                          {project.has_print_area && (
                            <Tooltip title="Has print area">
                              <CropFreeIcon sx={{ fontSize: 14, color: '#10B981' }} />
                            </Tooltip>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Project Name Dialog */}
      <Dialog
        open={showNewProjectDialog}
        onClose={() => setShowNewProjectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newProjectName.trim()) {
                handleCreateProject();
              }
            }}
            placeholder="e.g., Highway 101 Lane Closure"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewProjectDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateProject}
            disabled={!newProjectName.trim() || creating}
            sx={{ textTransform: 'none' }}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectSelector;
